import type {
  B2bCampaignActionHandler,
  B2bContract,
  B2bContractStatus,
  B2bContractType,
  B2bMatchingContributionPaymentFrequency,
  B2bPaperTransactionStatementFrequency,
} from '@Common/types/bo/b2b/contracts';
import { B2bContractFees } from '@Common/types/bo/b2b/contracts/fees';

const feeFields = {
  company: {
    file_opening: 'File opening',
    calculating_individual_allocations: 'Calculating individual allocations (per operation)',
    integration_of_beneficiary_data: 'Integration of beneficiary data (per file)',
    integration_of_affiliates_choices_via_mail:
      'Integration of affiliates choices via mail (per operation)',
    integration_of_affiliates_consultation_data:
      'Integration of your Affiliates consultation data outside the Company Dashboard (per file)',
    rejection_of_direct_debit_or_bank_transfer:
      'Rejection of direct debit or bank transfer (per incident)',
    extra_reminder_for_unpaid_invoices: 'Reminder for unpaid invoices, after 2 reminders',
    collective_transfer_to_another_manager:
      'Collective transfer to another manager (per account up to a max. of €1,000 per company)',
  } satisfies Record<keyof B2bContractFees['company'], string>,
  affiliate: {
    statements_and_certificates: 'Statements and certificates (IFU, transaction statement, etc.)',
    annual_custody_for_former_or_retired_employees:
      'Annual Custody fees for former employees or retirees',
    annual_custody_pereco_pct: 'PERECO account management fees for former employees or retirees',
    annual_custody_pereco_max: 'PERECO account management fee cap for former employees or retirees',
    account_position_at_given_date: 'Account position at a given date (excluding inheritance)',
    undelivered_mail_handling: 'Handling undelivered mail',
    pledging_or_release_of_assets: 'Pledging or release of your assets',
    search_for_documents_or_transactions_before_10_years:
      'Search for specific documents or transactions (cheques issued, transfers rejected, etc.) less than 10 years old',
    search_for_documents_or_transactions_after_10_years:
      'Search for specific documents or transactions (cheques issued, transfers rejected, etc.) dating back more than 10 years',
    transfer_from_pee_to_another_manager:
      'Individual transfer out of the PEE to another manager (per transfer)',
    transfer_from_pereco_to_another_manager_pct:
      'Individual transfer from PERECO to another manager',
    estate_settlement_low_amount: 'Estate settlement between €500 and €5,000',
    estate_settlement_high_amount: 'Estate settlement > €5,000',
    account_closure: 'Account closure (handling fee + 30-year archiving)',
    specific_mail_requests_processing: 'All processing of specific requests by mail',
    caisse_des_depots_recording: 'Consignment to Caisse des Dépôts',
    voluntary_payment_by_cheque: 'Voluntary payment by cheque processing',
    repayment_of_savings: 'Repayment of your savings (available and/or unavailable)',
    retractation_following_pereco_payment: 'Retraction following a PERECO payment',
    direct_debit_rejection: 'Direct debit rejection',
    unpaid_cheques: 'Fees for unpaid cheques',
    repayment_reimbursement_triggered: 'Reimbursement with a trigger threshold',
    repayment_by_cheque: 'Payment by cheque',
    repayment_by_bank_transfer: 'Payment by bank transfer',
    repayment_settlement_reissuing: 'Reissuing a settlement',
  } satisfies Record<keyof B2bContractFees['affiliate'], string>,
  static: {
    consultation_of_affiliates_per_mail: {
      label: 'Consultation of affiliates per mail',
      value: 'Actual cost',
    },
    time_savings_account_gateway: {
      label: 'Time savings account gateway',
      value: 'On request',
    },
  },
};

export const b2bContractsTranslations = {
  fields: {
    id: 'Contract ID',
    b2b_company_id: 'Company ID',
    team: 'Team',
    contract_number: 'Contract number',
    wrong_contract_number: 'Only letters, digits, underscores and hyphens are allowed.',
    contract_type: 'Contract type',
    label: 'Label',
    support_phone_number: 'Support phone number',
    insurer_company: 'Insurer Company',
    status: 'Contract status',
    signature_date: 'Contract signature date',
    effective_date: 'Effective date',
    end_date: 'Contract end date',
    is_active: 'Is active',
    relationship_type: 'Relationship type',
    paper_transaction_statement_frequency:
      'Frequency for the paper version of the transaction statements',
    version: 'Version',
    version_label: 'Version label',
    is_group_contract: 'Is the contract a Group contract',
    is_agreement_common_for_group: 'Is the agreement common for the group?',
    participation: {
      agreement_starting_date: 'Profit-sharing agreement start date',
      agreement_end_date: 'Profit-sharing agreement end date',
      is_agreement_common_for_group: 'Is the profit-sharing agreement common for the group?',
      distribution_uniformity_pct: 'Uniformly',
      distribution_salary_pct: 'Proportionally to salary received',
      distribution_tenure_pct: 'Proportionally to time of presence',
    },
    profit_sharing: {
      agreement_starting_date: 'Incentive agreement start date',
      agreement_end_date: 'Incentive agreement end date',
      is_tacitly_renewed: 'Is the incentive agreement tacitly renewed?',
      is_agreement_common_for_group: 'Is the incentive agreement common for the group?',
      distribution_uniformity_pct: 'Uniformly',
      distribution_salary_pct: 'Proportionally to salary received',
      distribution_tenure_pct: 'Proportionally to time of presence',
    },
    value_sharing: {
      agreement_starting_date: 'Value-sharing bonus agreement start date',
      agreement_end_date: 'Value-sharing bonus agreement end date',
      is_agreement_common_for_group: 'Is the value-sharing bonus agreement common for the group?',
    },
    unused_vacation_days: {
      agreement_starting_date: 'CET agreement start date',
      agreement_end_date: 'CET agreement end date',
      is_agreement_common_for_group: 'Is CET agreement common for the group?',
      increment_step: 'Increment in steps of',
    },
    monetized_days_off: {
      increment_step: 'Increment in steps of',
    },
    pee_agreement: {
      agreement_starting_date: 'PEE agreement start date',
      agreement_end_date: 'PEE agreement end date',
      is_agreement_common_for_group: 'Is PEE agreement common for the group?',
    },
    pereco_agreement: {
      agreement_starting_date: 'PERECO agreement start date',
      agreement_end_date: 'PERECO agreement end date',
      is_agreement_common_for_group: 'Is PERECO agreement common for the group?',
    },
    pero_agreement: {
      agreement_starting_date: 'PERO agreement start date',
      agreement_end_date: 'PERO agreement end date',
      is_agreement_common_for_group: 'Is PERO agreement common for the group?',
    },
    transfer: {
      is_competition_transfer: 'Transfer from the competition',
      external_custodian: 'Transfer from custodian',
      external_custodian_select: 'Custodian',
      external_custodian_other: 'Other custodian',
      is_soles_transfer: 'Transfer from former AXA Standard to SOLES',
    },
    campaigns: {
      allocation_computed_by: 'Allocations computed by',
      choice_collected_by: 'Choices collected by',
      payout_by: 'Payouts by',
      matching_contribution_computed_by: 'Matching contribution computed by',
    },
    created_at: 'Created at',
    updated_at: 'Updated at',
    dist_case_number: 'Case number',
    dist_case_reception_date: 'Case reception date',
    dist_correspondent_id: 'Distributor',
    dist_correspondent_company_name: 'Company name',
    dist_correspondent_assistance_email: 'Assistance email',
    dist_correspondent_firstname: 'First name',
    dist_correspondent_lastname: 'Last name',
    dist_correspondent_portfolio_code: 'Portfolio code',
    dist_correspondent_orias_code: 'ORIAS code',
    dist_correspondent_fia_code: 'FIA code',
    dist_correspondent_email: 'Email',
    dist_correspondent_landline: 'Landline phone number',
    dist_correspondent_phone_number: 'Mobile phone number',
    dist_correspondent_collective_inspector_email: 'Email from the collective inspector',
    dist_correspondent_street: 'Street',
    dist_correspondent_postal_code: 'Postal code',
    dist_correspondent_city: 'City',
    dist_correspondent_country: 'Country',
    dist_status: 'Status',
    matching_contribution_cut_off_date: 'Matching contribution cutoff date (DD/MM)',
    matching_contributions_enabled: 'Matching contributions enabled',
    matching_contribution_payment_frequency: 'Matching contribution payment frequency',
    is_matching_contribution_displayed: 'Is matching contribution displayed to Employees',
    total_contribution_ceiling: 'Total Matching Contribution ceiling across plans',
    is_liv: 'Is LIV',
    filib_url: 'FILIB URL',
    offer_code: 'Offer code',
    fees: feeFields,
    ghost_login: 'Ghost login',
  },
  initial_questions: {
    participation: 'Does the company have a profit-sharing agreement?',
    value_sharing: 'Does the company have a value-sharing bonus agreement?',
    profit_sharing: 'Does the company have an incentive agreement?',
    unused_vacation_days: 'Does the company have a CET agreement?',
    pee_agreement: 'Does the company have a PEE agreement?',
    pereco_agreement: 'Does the company have a PERECO agreement?',
    pero_agreement: 'Does the company have a PERO agreement?',
    transfer: 'Does the company have a transfer agreement?',
    participation_is_agreement_common_for_group:
      'Is the profit-sharing agreement common for the group?',
    value_sharing_is_agreement_common_for_group:
      'Is the value-sharing bonus agreement common for the group?',
    profit_sharing_is_agreement_common_for_group:
      'Is the incentive agreement common for the group?',
    unused_vacation_days_is_agreement_common_for_group:
      'Is the CET agreement common for the group?',
    pee_is_agreement_common_for_group: 'Is the PEE agreement common for the group?',
    pereco_is_agreement_common_for_group: 'Is the PERECO agreement common for the group?',
    pero_is_agreement_common_for_group: 'Is the PERO agreement common for the group?',
  },
  choices: {
    relationship_type: {
      direct: 'Direct',
      indirect: 'Indirect',
    } satisfies Record<B2bContract['relationship_type'], string>,
    campaign_action_handler: {
      distributor: 'Account keeper',
      company: 'Company',
    } satisfies Record<B2bCampaignActionHandler, string>,
    days_increment_step: {
      quarter_day: '0.25',
      half_day: '0.5',
    },
    team: {
      standard: 'Standard',
      middle: 'Middle (SME)',
      key_account: 'Key account',
      international: 'International',
    },
    paper_transaction_statement_frequency: {
      digital_only: 'Digital only',
      '1_month': '1 month',
      '3_months': '3 months',
      '6_months': '6 months',
      '12_months': '12 months',
      every_time: 'Every time',
    } satisfies Record<B2bPaperTransactionStatementFrequency, string>,
    matching_contribution_payment_frequency: {
      '1_month': '1 month',
      '3_months': '3 months',
      '6_months': '6 months',
      '12_months': '12 months',
    } satisfies Record<B2bMatchingContributionPaymentFrequency, string>,
    contract_type: {
      soles: 'SOLES',
      es_custom: 'ES custom',
      er_standard: 'ER standard',
      er_custom: 'ER custom',
      er_external: 'ER External',
      essentiel: 'Essentiel offer',
      flexible: 'Flexible offer',
      patrimonial: 'Patrimonial offer',
      premium: 'Premium offer',
      sur_mesure: 'Malakoff Humanis custom',
    } satisfies Record<B2bContractType, string>,
    status: {
      offer: 'Offer',
      effective: 'Effective',
      suspended: 'Suspended',
      denounced: 'Denounced',
      process_of_creation: 'In process of creation',
      closed_by_amendment: 'Closed by amendment',
      other: 'Other',
      terminated: 'Terminated',
    } satisfies Record<B2bContractStatus, string>,
    custody_fees_amounts_per: {
      employees: 'Employees',
      products: 'Products',
      flat_rate: 'Flat rate',
    },
  },
  menus: {
    details: 'Details',
    agreements: 'Agreements info',
    campaigns: 'Operations processing',
    plans: 'Products',
    documents: 'Documents',
    matching_contributions: 'Matching contributions',
    fees: 'Fees',
    distributor: {
      label: 'Distributor Info',
      headers: {
        distributor_correspondent: 'Distributor Correspondent Details',
        address: 'Address',
      },
    },
  },
  sections: {
    participation: 'Profit-sharing bonus',
    participation_distribution: 'Profit-sharing distribution details',
    profit_sharing: 'Incentive bonus',
    profit_sharing_distribution: 'Incentive bonus distribution details',
    value_sharing: 'Value-sharing bonus',
    voluntary_deposit: "Company's voluntary deposits",
    unused_vacation_days: 'Time savings account bonus',
    monetized_days_off: 'Monetized days-off',
    pee_agreement: 'PEE (PEE / PEI / PEEG)',
    pereco_agreement: 'PERECO (PERECO / PERECO-I / PERECOG)',
    pero_agreement: 'PERO (PERO / PERU) ',
    transfer: 'Transfer',
    amendment: 'Amendment',
    no_agreement: 'No agreement',
  },
  campaigns: {
    has_processing_options_by_cash_sources:
      'Do you want to configure the operations processing parameters for each cash source ?',
    processing_options_by_cash_sources_next_step:
      'The setting will be done per cash source in the next step.',
    matching_contribution_computed_by_edit_warning:
      'You changed who computes the matching contribution. Remember to also update the plans configuration accordingly.',
    processing_options_edit_warning:
      'This change will not retroactively impact any currently running campaign using this contract.',
    all: 'All operations processing (P+I+PPV)',
    participation: 'Profit-sharing bonus',
    profit_sharing: 'Incentive bonus',
    value_sharing_bonus: 'Value-sharing bonus',
    voluntary_deposit: "Company's voluntary deposits",
    unused_vacation_days: 'Time savings account bonus',
  },
  matching_contributions: {
    disable_edit_warning:
      'Disabling matching contributions will not cancel pending matching contributions: they will be charged next month.',
  },
  distributor_info: {
    case: 'Case details',
    correspondent: 'Distributor correspondent details',
    distributor: 'Distributor',
    address: 'Address',
    mailing_address: 'Mailing Address',
    search_company: 'Enter distributor company name',
    search_company_label: 'Distributor company name',
    create_new_hint: "Can't find a distributor? Create one in the",
    create_new_link: 'Distributors section',
    view_distributor_link: 'View distributor',
  },
  amendment: {
    title: 'Contract amendment',
    label: 'Label',
    version: 'Version number',
    signed_amendment_document: 'Upload signed contract amendment*',
    notes: 'Notes',
    applies_at: 'Effective date for the change',
  },
  distribution_details: {
    rule: 'Rule',
    pct: '%',
    total: 'Total',
  },
  fees: {
    sections: {
      details: 'Fees details',
      custody: 'Custody fees',
      company: 'Additional costs paid by the company',
      collective_operation: 'Collective operation related',
      amount_excl_vat: 'Amount excl. VAT',
      amount_incl_vat: 'Amount inc. taxes',
      others: 'Others',
      affiliate: 'Additional costs paid by the affiliate',
      savings_management: 'Savings management',
      current_operations: 'Current operations',
      repayment: 'Repayment transactions',
      amounts_per: 'Amounts per:',
      fee_code: 'Fee code',
      use_old_pricing: 'Use the old pricing grid?',
      warning:
        'If savings are only in PERECO, the custody fees paid by the affiliate will be set to 5% (capped at €20).',
    },
    subtitles: {
      eur_vat_excluded: 'All amounts are in € and VAT excluded',
      eur_vat_included: 'All amounts are in € and incl. taxes',
      free_if_done_online: 'Free if done online',
      amounts_per_employees: 'Amounts are per employees',
      amounts_per_products: 'Amounts are per products',
      amounts_per_flat_rate: 'Amounts are per flat rate',
      old_pricing: 'Old pricing grid',
    },
    custody: {
      amount: {
        header: 'Average Outstanding amount',
        col1: 'Workforce',
        col2: '<= 15 000€',
        col3: '> 15 000€',
        row1: '[1 - 11]',
        row2: '[12 - 249+]',
      },
      paid_by: {
        employee_status: 'Employee status',
        paid_by: 'Paid by',
        current: 'Current',
        past: 'Past',
        retired: 'Retired',
        amount: 'Amount',
        amount_pee: 'Amount per PEE',
        amount_pereco: 'Amount per PERECO',
        amount_incl_vat: '(incl. taxes)',
        amount_excl_vat: '(excl. VAT)',
      },
    },
    flat_rate: {
      fee_label: 'FE_forfait_pee_pereco',
    },
    old_pricing: {
      flat_rate: 'FE_bse_forfait_tc',
      fees_amount: 'FE_bse_frais_tc',
    },
  },
  actions: {
    create: {
      title: 'Create Contract',
      success: 'Contract created',
      failure: 'Contract failed to be created: %{error}',
      steps: {
        '1': 'General information',
        '2': 'Distributor info',
        '3': 'Agreements details',
        '4': 'Operations processing',
        '5': 'Fees',
      },
    },
    edit: {
      simple: {
        button: 'Edit contract',
        title: 'Edit contract',
      },
      button: 'Create amendment',
      title: 'Edit Contract',
      success: 'Contract edited',
      failure: 'Contract failed to be edited: %{error}',
      steps: {
        '1': 'Fees',
        '2': 'Amendment',
      },
    },
  },
  validation: {
    distribution_total_pct: 'Total must be 100%',
    participation:
      'If at least one profit-sharing agreement field is completed, all fields must be completed.',
    profit_sharing:
      'If at least one incentive agreement field is completed, all fields must be completed.',
    transfer: 'At least one of the fields must be selected',
    dd_mm_date: 'Must be a date with format DD/MM',
    agreement_starting_date: 'Start date should be inferior to end date',
    agreement_end_date: 'End date should be superior to start date',
  },
  documents: {
    title: 'Documents',
    empty: 'This contract does not yet have any documents',
    labels: {
      created_at: 'Created at',
      type: 'Type',
      name: 'Name',
      id: 'Document ID',
      url: 'Url',
    },
    button: 'Upload document',
    create: {
      title: 'Upload Contract Document',
      success: 'Document Uploaded',
      failure: 'Error uploading document: %{error}',
      label: 'Upload Document',
    },
    type: {
      employee_savings_booklet: 'Employee Saving Booklet',
      general_terms_and_conditions: 'General Terms and Conditions',
      plan_regulations: 'Plan Regulations',
      amendments: 'Amendments',
      price_list_employee: 'Price List Employee',
      price_list_company: 'Price List Company',
      signed_contract_amendment: 'Signed Contract Amendment',
    },
  },
};
